/**
 * Tippfehler und Kundenwörter in der Suche auffangen.
 *
 * Shopify verzeiht von sich aus nur wenig: Wortanfänge und gelegentlich einen
 * vertauschten Buchstaben. Gemessen am PRESCH-Katalog (316 Produkte):
 *
 *   "hamer"            → 3 Treffer   (Shopify fängt das selbst)
 *   "winkl"            → 5 Treffer   (dito)
 *   "zagne"            → 0 Treffer
 *   "saege"            → 0 Treffer,  "säge" → 1
 *   "schraubenzieher"  → 0 Treffer,  "schraubendreher" → 4
 *   "wasserpumpenzange"→ 0 Treffer,  "zange" → 7
 *
 * Die letzten vier Fälle sind der Grund für diese Datei. Sie zerfallen in drei
 * Gruppen: echte Tippfehler, umschriebene Umlaute und Kundenwörter, die der
 * Katalog anders nennt.
 *
 * **Vorgehen: großzügig raten, streng prüfen.** Aus der leer gelaufenen Eingabe
 * werden Kandidaten gebildet — Umlenkungen, Umlautvarianten, vertauschte und
 * ausgelassene Buchstaben, bei Zusammensetzungen auch das Grundwort am Ende.
 * Jeder Kandidat wird gegen `/search/suggest.json` geprüft, und nur wer wirklich
 * Treffer liefert, darf angezeigt werden. Damit kann kein Vorschlag ins Leere
 * führen — und ein unsinnig verdrehter Kandidat fällt von allein durch.
 *
 * Das erlaubt es, beim Raten unsauber zu sein: "quer" → "qür" kostet eine
 * Anfrage und verschwindet, statt Regeln zu erfordern, die jeden Sonderfall
 * kennen.
 *
 * **Warum erst nach der Antwort und nicht vor der Anfrage:** Wer tippt, soll
 * seinen eigenen Text im Feld sehen. Die Korrektur greift deshalb erst, wenn
 * Shopify für die getippte Eingabe nichts gefunden hat — erkennbar am Ereignis
 * `shopify:search:update`, dessen Promise auf `{ totalCount }` auflöst.
 *
 * **Warum ein eigenes Element und kein Aufruf beim Laden:** Die Kopfleiste wird
 * nachträglich hydriert und tauscht dabei ihre Knoten aus. Ein einmal
 * angehängter Zuhörer wäre danach weg. `connectedCallback` läuft nach dem
 * Austausch erneut — der Zuhörer hängt also immer am aktuellen Knoten.
 */

import { morph } from '@theme/morph';

/**
 * Durchsuchte Felder. Muss mit `assets/predictive-search.js` übereinstimmen —
 * dort steht die Begründung für genau diese drei. Weicht die Liste ab, prüft die
 * Korrektur gegen einen anderen Index als den, der danach angezeigt wird: ein
 * Vorschlag könnte acht Treffer melden und eine leere Fläche rendern.
 *
 * Gemessen ohne diese Felder: `suggest.json` und die gerenderte Fläche stimmen
 * für „zange", „säge", „schraubendreher", „inbus", „zollstock" und
 * „stechbeitel" auf den Treffer überein. Mit abweichender Liste wich „zange" um
 * 8 zu 1 ab.
 */
const SUCHFELDER = 'title,product_type,tag';

/** Kürzere Eingaben sind beim Tippen normal und kein Tippfehler. */
const MINDESTLAENGE = 4;

/** Abstand nach der leeren Antwort, damit Zwischenstände beim Tippen ruhen. */
const WARTEZEIT = 350;

/** Obergrenzen je Durchgang, damit eine leere Eingabe nicht zur Anfragenflut wird. */
const GRENZE_NAHELIEGEND = 12;
const GRENZE_LETZTE_HILFE = 12;

/** Gemerkte Trefferzahlen je Begriff, gilt für die Lebensdauer der Seite. */
const geprueft = new Map();

/**
 * Routen aus dem Theme statt festverdrahtet, damit übersetzte Märkte stimmen:
 * dort heißt die Suche `/fr/search` und nicht `/search`.
 *
 * `Theme` ist ein globales `let` und liegt NICHT auf `window` — `window.Theme`
 * ist nachgemessen `undefined`, `typeof Theme` dagegen `object`. Der Zugriff
 * läuft deshalb über den nackten Namen samt `typeof`-Absicherung, sonst wirft
 * das Modul einen ReferenceError, falls `scripts.liquid` einmal nicht lädt.
 *
 * @param {'search_url' | 'predictive_search_url'} name
 * @param {string} ersatz
 * @returns {string}
 */
function route(name, ersatz) {
  const routen = typeof Theme !== 'undefined' ? /** @type {any} */ (Theme)?.routes : undefined;
  return routen?.[name] || ersatz;
}

/**
 * Stufen der Kandidaten. Kleinere Zahl heißt: näher an dem, was gemeint war.
 * Die Stufe entscheidet über die Rangfolge UND über den Wortlaut des Hinweises:
 * eine Umlenkung ist eine Korrektur, ein abgeschnittenes Grundwort nur ein
 * Näherungswert. Beides als „Meinten Sie" auszugeben wäre eine Behauptung, die
 * das zweite nicht deckt.
 */
const STUFE = {
  UMLENKUNG: 1,
  UMLAUT: 2,
  VERTAUSCHT: 3,
  AUSGELASSEN: 4,
  GRUNDWORT: 5,
  WORTANFANG: 6,
};

/** Ab dieser Stufe ist es keine Korrektur mehr, sondern ein Näherungswert. */
const STUFE_NAEHERUNG = STUFE.GRUNDWORT;

/**
 * @typedef {object} Kandidat
 * @property {string} begriff - Die vollständige Suchanfrage mit ersetztem Wort.
 * @property {string} wort - Nur das ersetzte Wort, für den Hinweistext.
 * @property {number} stufe - Eine der STUFE-Konstanten.
 */

/**
 * Umlenkungen aus den Theme-Einstellungen lesen: eine Regel je Zeile,
 * `eingabe = ziel`. Zeilen mit `#` sind Notizen.
 *
 * @param {Element | null} quelle - Das JSON-Script mit dem Rohtext.
 * @returns {Array<[string, string]>} Paare aus Eingabe und Ziel.
 */
function umlenkungenLesen(quelle) {
  if (!quelle?.textContent) return [];

  let rohtext = '';
  try {
    rohtext = JSON.parse(quelle.textContent) ?? '';
  } catch {
    return [];
  }

  return rohtext
    .split('\n')
    .map((zeile) => zeile.trim())
    .filter((zeile) => zeile && !zeile.startsWith('#') && zeile.includes('='))
    .map((zeile) => {
      const [eingabe, ziel] = zeile.split('=');
      return /** @type {[string, string]} */ ([eingabe.trim().toLowerCase(), ziel.trim().toLowerCase()]);
    })
    .filter(([eingabe, ziel]) => eingabe.length > 2 && ziel.length > 1);
}

/**
 * Das längste Wort der Eingabe. Tippfehler sitzen im tragenden Wort, nicht in
 * „set" oder „mm" — und ein Wort zu ersetzen ist ungleich billiger, als alle
 * Wörter gleichzeitig zu variieren.
 *
 * @param {string} eingabe
 * @returns {string}
 */
function tragendesWort(eingabe) {
  return eingabe.split(/\s+/).reduce((laengstes, wort) => (wort.length > laengstes.length ? wort : laengstes), '');
}

/**
 * Umlautschreibweisen in beide Richtungen: „saege" ↔ „säge", „massband" ↔
 * „maßband". Shopify faltet das nicht — nachgemessen: „säge" 1 Treffer,
 * „saege" 0.
 *
 * `ue` bleibt nach `q` unangetastet, sonst würde aus „quer" ein „qür". Alle
 * übrigen Fehlgriffe darf die Prüfung aussortieren; dieser hier ist nur zu
 * häufig, um ihn jedes Mal zu bezahlen.
 *
 * @param {string} wort
 * @returns {string[]}
 */
function umlautVarianten(wort) {
  const varianten = new Set();
  /** @type {Array<[RegExp, string]>} */
  const regeln = [
    [/(?<!q)ue/g, 'ü'],
    [/ae/g, 'ä'],
    [/oe/g, 'ö'],
    [/ss/g, 'ß'],
    [/ü/g, 'ue'],
    [/ä/g, 'ae'],
    [/ö/g, 'oe'],
    [/ß/g, 'ss'],
  ];

  for (const [suchen, ersetzen] of regeln) {
    const variante = wort.replace(suchen, ersetzen);
    if (variante !== wort) varianten.add(variante);
  }

  return [...varianten];
}

/**
 * Zwei benachbarte Buchstaben getauscht — der häufigste Tippfehler überhaupt
 * („zagne" statt „zange").
 *
 * @param {string} wort
 * @returns {string[]}
 */
function vertauschteBuchstaben(wort) {
  const varianten = [];
  for (let i = 0; i < wort.length - 1; i++) {
    if (wort[i] === wort[i + 1]) continue;
    varianten.push(wort.slice(0, i) + wort[i + 1] + wort[i] + wort.slice(i + 2));
  }
  return varianten;
}

/**
 * Ein Buchstabe zu viel — deckt auch doppelt getippte Buchstaben ab
 * („hammmer").
 *
 * @param {string} wort
 * @returns {string[]}
 */
function ausgelasseneBuchstaben(wort) {
  const varianten = new Set();
  for (let i = 0; i < wort.length; i++) {
    varianten.add(wort.slice(0, i) + wort.slice(i + 1));
  }
  return [...varianten];
}

/**
 * Kandidaten bilden, nach Stufe geordnet und gegen Doppelte gesichert.
 *
 * @param {string} eingabe - Die normalisierte Suchanfrage.
 * @param {Array<[string, string]>} umlenkungen
 * @param {boolean} letzteHilfe - Zweiter Durchgang: Grundwort und Wortanfang.
 * @returns {Kandidat[]}
 */
function kandidatenBilden(eingabe, umlenkungen, letzteHilfe) {
  const wort = tragendesWort(eingabe);
  /** @type {Kandidat[]} */
  const kandidaten = [];
  const gesehen = new Set([eingabe]);

  /** @param {string} ersatz @param {number} stufe */
  const aufnehmen = (ersatz, stufe) => {
    if (ersatz.length < 3) return;
    const begriff = eingabe === wort ? ersatz : eingabe.replace(wort, ersatz);
    if (gesehen.has(begriff)) return;
    gesehen.add(begriff);
    kandidaten.push({ begriff, wort: ersatz, stufe });
  };

  if (!letzteHilfe) {
    // Umlenkungen greifen auf der ganzen Eingabe und als Teilwort, damit
    // „kreuzschraubenzieher" über die Regel „schraubenzieher = schraubendreher"
    // mitläuft, ohne dass jede Zusammensetzung einzeln gepflegt werden muss.
    for (const [von, nach] of umlenkungen) {
      if (eingabe.includes(von)) {
        const begriff = eingabe.replaceAll(von, nach);
        if (!gesehen.has(begriff)) {
          gesehen.add(begriff);
          kandidaten.push({ begriff, wort: nach, stufe: STUFE.UMLENKUNG });
        }
      }
    }

    for (const variante of umlautVarianten(wort)) aufnehmen(variante, STUFE.UMLAUT);
    for (const variante of vertauschteBuchstaben(wort)) aufnehmen(variante, STUFE.VERTAUSCHT);
    for (const variante of ausgelasseneBuchstaben(wort)) aufnehmen(variante, STUFE.AUSGELASSEN);

    return kandidaten.slice(0, GRENZE_NAHELIEGEND);
  }

  // Zweiter Durchgang. Deutsche Zusammensetzungen tragen das Grundwort hinten:
  // aus „wasserpumpenzange" wird „zange". Das ist keine Korrektur mehr, sondern
  // ein Angebot — deshalb eigene Stufe und eigener Wortlaut im Hinweis.
  //
  // Beides ist selbstsichernd, weil Shopify nur Wortanfänge trifft: „zange"
  // findet „Zangen Set", ein Bruchstück wie „nzange" findet nichts und fällt
  // bei der Prüfung durch.
  for (let laenge = Math.min(10, wort.length - 1); laenge >= 4; laenge--) {
    aufnehmen(wort.slice(-laenge), STUFE.GRUNDWORT);
  }

  // Und zuletzt der Wortanfang: „schraubnzieher" (zwei Fehler auf einmal) ist
  // über Vertauschen oder Auslassen nicht zu retten, „schraub" findet den
  // Schraubendreher aber sofort.
  //
  // Bei neun Zeichen ist Schluss, und das ist keine Sparmaßnahme: Längere
  // Anfänge kann Shopify schon selbst, es gleicht die Eingabe ohnehin gegen
  // Wortanfänge ab. Nur wenn der Fehler INNERHALB des Wortes sitzt, hilft
  // Abschneiden — und dann muss man weit genug abschneiden. Ohne diese Grenze
  // fraßen „schraubnziehe" bis „schraubnz" den Deckel auf, und „schraub" kam
  // nie zum Zug: nachgemessen blieb „schraubnzieher" ohne Vorschlag.
  for (let laenge = Math.min(9, wort.length - 1); laenge >= 5; laenge--) {
    aufnehmen(wort.slice(0, laenge), STUFE.WORTANFANG);
  }

  return kandidaten.slice(0, GRENZE_LETZTE_HILFE);
}

/**
 * Trefferzahl eines Begriffs. Bewusst gegen `suggest.json` und nicht gegen die
 * gerenderte Sektion: Die Prüfung läuft für bis zu zwölf Kandidaten
 * gleichzeitig, da zählt jedes Kilobyte.
 *
 * @param {string} begriff
 * @param {AbortSignal} signal
 * @returns {Promise<number>}
 */
async function trefferZaehlen(begriff, signal) {
  const gemerkt = geprueft.get(begriff);
  if (gemerkt !== undefined) return gemerkt;

  const url = new URL(`${route('predictive_search_url', '/search/suggest')}.json`, location.origin);
  url.searchParams.set('q', begriff);
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('resources[limit]', '4');
  url.searchParams.set('resources[options][fields]', SUCHFELDER);

  try {
    const antwort = await fetch(url, { signal });
    if (!antwort.ok) return 0;
    const daten = await antwort.json();
    const anzahl = daten?.resources?.results?.products?.length ?? 0;
    geprueft.set(begriff, anzahl);
    return anzahl;
  } catch {
    return 0;
  }
}

/**
 * Aus geprüften Kandidaten den besten wählen: erst die Stufe, dann die
 * Trefferzahl, dann der LÄNGERE Begriff.
 *
 * Die Länge entscheidet bei abgeschnittenen Wörtern, und dort ist länger
 * genauer: „schraub" und „schra" finden beide den Schraubendreher, aber nur das
 * erste sagt dem Kunden noch, wovon die Rede ist.
 *
 * @param {Array<Kandidat & {treffer: number}>} geprueftListe
 * @returns {(Kandidat & {treffer: number}) | null}
 */
function bestenWaehlen(geprueftListe) {
  const brauchbar = geprueftListe.filter((k) => k.treffer > 0);
  if (!brauchbar.length) return null;

  brauchbar.sort(
    (a, b) => a.stufe - b.stufe || b.treffer - a.treffer || b.begriff.length - a.begriff.length
  );
  return brauchbar[0];
}

/**
 * Nur für die Anzeige: Der geprüfte Begriff läuft intern klein durch die Suche,
 * aber „Meinten Sie säge?" liest sich im Deutschen falsch — Werkzeuge sind
 * Substantive. Die Suche selbst bleibt unberührt, Shopify unterscheidet dort
 * keine Groß- und Kleinschreibung.
 *
 * @param {string} begriff
 * @returns {string}
 */
function grossGeschrieben(begriff) {
  return begriff.replace(/(^|\s)(\p{Ll})/gu, (_, davor, buchstabe) => davor + buchstabe.toUpperCase());
}

class SearchTypoHelp extends HTMLElement {
  /** @type {Element | null} */
  #komponente = null;

  /** @type {HTMLInputElement | null} */
  #feld = null;

  /** @type {Element | null} */
  #ergebnisse = null;

  /** @type {AbortController | null} */
  #laufend = null;

  /** @type {number | undefined} */
  #timer;

  /** @type {Array<[string, string]>} */
  #umlenkungen = [];

  /** Aktuell angezeigte Korrektur — Ziel für Enter und „alle Ergebnisse". */
  #korrektur = '';

  connectedCallback() {
    // Dokumentweit gesucht, weil das JSON in der Klappfläche neben der
    // Komponente steht, auf der Ergebnisseite aber neben diesem Element.
    this.#umlenkungen = umlenkungenLesen(document.querySelector('[data-search-aliases]'));

    // Auf der Ergebnisseite gibt es keine Klappfläche, die man füllen könnte.
    // Dort ist die Suche schon gelaufen und leer ausgegangen — es braucht nur
    // den Satz mit dem Link.
    if (this.hasAttribute('data-standalone')) {
      this.#eigenstaendig();
      return;
    }

    this.#komponente = this.closest('predictive-search-component');
    if (!this.#komponente) return;

    this.#feld = this.#komponente.querySelector('input[name="q"]');
    this.#ergebnisse = this.#komponente.querySelector('[ref="predictiveSearchResults"]');

    if (!this.#feld || !this.#ergebnisse) return;

    this.#komponente.addEventListener('shopify:search:update', this.#beiSuchlauf);

    // Enter und „alle Ergebnisse" führen sonst auf eine leere Ergebnisseite,
    // weil dort die getippte Fassung ankommt. Solange ein Vorschlag steht,
    // zeigen beide auf den geprüften Begriff.
    this.#komponente.addEventListener('submit', this.#beiAbsenden, { capture: true });
    this.#komponente.addEventListener('click', this.#beiKlick, { capture: true });
  }

  disconnectedCallback() {
    this.#komponente?.removeEventListener('shopify:search:update', this.#beiSuchlauf);
    this.#komponente?.removeEventListener('submit', this.#beiAbsenden, { capture: true });
    this.#komponente?.removeEventListener('click', this.#beiKlick, { capture: true });
    clearTimeout(this.#timer);
    this.#laufend?.abort();
  }

  /** @param {Event & {search?: {query?: string}, promise?: Promise<{totalCount: number}>}} event */
  #beiSuchlauf = (event) => {
    const eingabe = (event.search?.query ?? '').trim();

    // Jede neue Anfrage macht den alten Hinweis ungültig, auch die laufende
    // Prüfung dazu.
    this.#aufraeumen();

    if (eingabe.length < MINDESTLAENGE || !event.promise) return;

    event.promise
      .then(({ totalCount }) => {
        if (totalCount > 0) return;
        if (this.#feld?.value.trim() !== eingabe) return;

        this.#timer = window.setTimeout(() => this.#korrigieren(eingabe), WARTEZEIT);
      })
      .catch(() => {
        /* Abgebrochene Suchläufe sind der Normalfall beim Tippen. */
      });
  };

  /** @param {Event} event */
  #beiAbsenden = (event) => {
    if (!this.#korrektur) return;
    event.preventDefault();
    location.assign(this.#sucheUrl(this.#korrektur));
  };

  /** @param {Event} event */
  #beiKlick = (event) => {
    if (!this.#korrektur) return;
    const ziel = /** @type {Element} */ (event.target);
    if (!ziel.closest?.('[ref="viewAllButton"]')) return;
    event.preventDefault();
    event.stopPropagation();
    location.assign(this.#sucheUrl(this.#korrektur));
  };

  /** @param {string} begriff */
  #sucheUrl(begriff) {
    const url = new URL(route('search_url', '/search'), location.origin);
    url.searchParams.set('q', begriff);
    return url.toString();
  }

  #aufraeumen() {
    clearTimeout(this.#timer);
    this.#laufend?.abort();
    this.#laufend = null;
    this.#korrektur = '';
    this.replaceChildren();
    this.hidden = true;
  }

  /**
   * Zwei Durchgänge: erst das Naheliegende, und nur wenn davon nichts trägt, die
   * letzte Hilfe. Wer sich bloß vertippt hat, bezahlt so nicht die Anfragen für
   * abgeschnittene Wörter mit.
   *
   * @param {string} eingabe
   * @param {AbortSignal} signal
   * @returns {Promise<(Kandidat & {treffer: number}) | null>}
   */
  async #bestenFinden(eingabe, signal) {
    const normalisiert = eingabe.toLowerCase().replace(/\s+/g, ' ');

    for (const letzteHilfe of [false, true]) {
      const kandidaten = kandidatenBilden(normalisiert, this.#umlenkungen, letzteHilfe);
      if (!kandidaten.length) continue;

      const geprueftListe = await Promise.all(
        kandidaten.map(async (kandidat) => ({
          ...kandidat,
          treffer: await trefferZaehlen(kandidat.begriff, signal),
        }))
      );

      if (signal.aborted) return null;

      const bester = bestenWaehlen(geprueftListe);
      if (bester) return bester;
    }

    return null;
  }

  /** @param {string} eingabe */
  async #korrigieren(eingabe) {
    const abbruch = new AbortController();
    this.#laufend = abbruch;

    const bester = await this.#bestenFinden(eingabe, abbruch.signal);
    if (!bester) return;
    if (abbruch.signal.aborted || this.#feld?.value.trim() !== eingabe) return;

    await this.#anzeigen(eingabe, bester, abbruch.signal);
  }

  /**
   * Ergebnisseite: Die Suche ist gelaufen und leer geblieben, der Kunde steht
   * vor der Ersatz-Collection. Ein Satz mit geprüftem Link ist hier mehr wert
   * als nachgeladene Karten — er sagt, welches Wort weiterhilft, und die
   * Adresse bleibt teilbar.
   *
   * Der Weg hierher ist der häufigste überhaupt: Enter im Suchfeld. Die
   * Klappfläche leitet Enter auf den geprüften Begriff um, aber nur, wenn sie
   * ihn schon kennt — wer schnell tippt und sofort Enter drückt, landet hier.
   */
  async #eigenstaendig() {
    const eingabe = (this.dataset.query ?? '').trim();
    if (eingabe.length < MINDESTLAENGE) return;

    const abbruch = new AbortController();
    this.#laufend = abbruch;

    const bester = await this.#bestenFinden(eingabe, abbruch.signal);
    if (!bester || abbruch.signal.aborted) return;

    const naeherung = bester.stufe >= STUFE_NAEHERUNG;

    const zeile = document.createElement('p');
    zeile.className = 'custom-search-typo-page';

    const einleitung = document.createElement('span');
    einleitung.textContent = naeherung ? 'Nichts Passendes gefunden. ' : 'Meinten Sie ';

    const verweis = document.createElement('a');
    verweis.href = this.#sucheUrl(bester.begriff);
    verweis.textContent = naeherung ? 'Ähnliche Werkzeuge ansehen' : grossGeschrieben(bester.begriff);

    zeile.append(einleitung, verweis, document.createTextNode(naeherung ? '' : '?'));

    this.replaceChildren(zeile);
    this.hidden = false;
  }

  /**
   * Die Ergebnisse des geprüften Begriffs holen und in die Ergebnisfläche
   * morphen — dieselbe Sektion und derselbe Weg, den die Komponente bei jedem
   * Tastendruck geht, nur mit anderem `q`. Deshalb tragen die Karten
   * unverändert die Auszeichnung des Themes.
   *
   * Die Suchroute ist Pflicht: `/?section_id=predictive-search` liefert
   * gemessene 81 Bytes ohne Ergebnisse, `/search/suggest?section_id=…` die
   * vollständige Fläche.
   *
   * @param {string} eingabe
   * @param {Kandidat & {treffer: number}} bester
   * @param {AbortSignal} signal
   */
  async #anzeigen(eingabe, bester, signal) {
    const sektion = /** @type {HTMLElement} */ (this.#komponente)?.dataset.sectionId;
    if (!sektion || !this.#ergebnisse) return;

    const url = new URL(route('predictive_search_url', '/search/suggest'), location.origin);
    url.searchParams.set('q', bester.begriff);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', SUCHFELDER);
    url.searchParams.set('section_id', sektion);

    let markup = '';
    try {
      const antwort = await fetch(url, { signal });
      markup = await antwort.text();
    } catch {
      return;
    }

    if (signal.aborted || this.#feld?.value.trim() !== eingabe || !markup) return;

    morph(this.#ergebnisse, markup);

    this.#korrektur = bester.begriff;
    this.#hinweisSetzen(eingabe, bester);
  }

  /**
   * @param {string} eingabe
   * @param {Kandidat & {treffer: number}} bester
   */
  #hinweisSetzen(eingabe, bester) {
    const naeherung = bester.stufe >= STUFE_NAEHERUNG;

    const zeile = document.createElement('p');
    zeile.className = 'custom-search-inline__typo';

    const getippt = document.createElement('span');
    getippt.className = 'custom-search-inline__typo-original';
    getippt.textContent = `Keine Treffer für „${eingabe}“.`;

    // Bei einer Umlenkung oder einem korrigierten Buchstaben ist der geprüfte
    // Begriff ein echtes Wort und wird genannt — er erklärt die Liste darunter.
    //
    // Bei einer Näherung ist er ein Bruchstück: „schraubnzieher" landet über den
    // Wortanfang bei „schraubn", und das sechs richtige Produkte findet, ist als
    // Suchbegriff aber kein Wort. So etwas als Vorschlag hinzuschreiben liest
    // sich wie ein Fehler im Shop. Die Ergebnisse sprechen dann für sich.
    if (naeherung) {
      const angebot = document.createElement('span');
      angebot.textContent = ' Das könnte passen:';
      zeile.append(getippt, angebot);
    } else {
      const vorschlag = document.createElement('span');
      vorschlag.textContent = ' Ergebnisse für ';

      const begriff = document.createElement('strong');
      begriff.textContent = grossGeschrieben(bester.begriff);

      zeile.append(getippt, vorschlag, begriff, document.createTextNode(':'));
    }

    this.replaceChildren(zeile);
    this.hidden = false;
  }
}

if (!customElements.get('search-typo-help')) {
  customElements.define('search-typo-help', SearchTypoHelp);
}
